import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Home,
  Search as SearchIcon,
  Users,
  Building,
  Download,
  Chrome,
  ArrowUpRight,
  Loader,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { posthog } from 'posthog-js';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { useSidebar } from '../../../components/ui/sidebar';
import type { URLParamsType } from '../../../types';
import type { RootState } from '../../../reducers';
import { getFolderUrl } from '../../../lib/routingUtils';
import FreeTrial from '../../plan/upgrade/FreeTrial';
import { useUserTags } from '../../../hooks/useTags';
import { useExtensionDetection } from '../../../hooks/useExtensionDetection';
import { setDrawerState } from '../dashboardSlice';
import { pushModal } from '../../globalModals/globalModalsSlice';
import SearchDialog from '../../search/SearchDialog';
import PeopleModal from '../../people/PeopleModal';
import CompaniesModal from '../../companies/CompaniesModal';
import { USER_PACKAGE } from '../../plan/userPackage';

interface NavigationItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  tooltip: string;
  href?: string;
  onClick?: () => void;
  isActive?: boolean;
  hidden?: boolean;
}

const NavigationActions: React.FC = () => {
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showPeopleModal, setShowPeopleModal] = useState(false);
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [loadingNavItem, setLoadingNavItem] = useState<string | null>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { team, library, plan } = useSelector((state: RootState) => state);
  const { setTag } = useUserTags();
  const { folderId } = useParams<URLParamsType>();
  const { selectedTeam, teams } = team;
  const teamId = selectedTeam?.teamId || (teams[0]?.teamId as string);
  const { state, isMobile: isMobileFromSidebar, openMobile } = useSidebar();
  const open = isMobileFromSidebar ? openMobile : state === 'expanded';

  const teamPackage = plan.subscription?.package;
  const isFreeUser =
    !teamPackage ||
    teamPackage === USER_PACKAGE.FREE ||
    teamPackage === USER_PACKAGE.FREE_LOGGEDIN;

  // Detect if Chrome extension is installed
  const isExtensionInstalled = useExtensionDetection();

  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Clear loading state when files finish loading
  React.useEffect(() => {
    if (
      library.files.status === 'succeeded' ||
      library.files.status === 'failed'
    ) {
      setLoadingNavItem(null);
    }
  }, [library.files.status]);

  const handleQuickActionClick = () => {
    // Set default view and sorting preferences using tags
    setTag({ key: 'viewMode', value: 'grid' });
    setTag({ key: 'sortBy', value: 'createdAt' });
    setTag({ key: 'sortOrder', value: 'desc' });

    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const handleSearchClick = () => {
    setShowSearchDialog(true);
    setLoadingNavItem(null);
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const handleDownloadAppClick = () => {
    // Open download app page or dialog
    window.open(
      'https://screenapp.io/mobile?utm_source=screenapp-product&utm_medium=drawer-actions&utm_campaign=link',
      '_blank',
    );
    setLoadingNavItem(null);
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const handleChromeExtensionClick = () => {
    // Open Chrome Web Store link for extension
    window.open(
      'https://screenapp.io/chrome?utm_source=screenapp-product&utm_medium=drawer-actions&utm_campaign=link',
      '_blank',
    );
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const handlePeopleClick = () => {
    posthog.capture('people-button-clicked', {
      teamId,
    });
    if (isFreeUser) {
      dispatch(
        pushModal({
          type: 'stripe-pricing-table-upgrade-modal',
          source: 'people',
        }),
      );
      return;
    }
    setShowPeopleModal(true);
    setLoadingNavItem(null);
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const handleCompaniesClick = () => {
    posthog.capture('companies-button-clicked', {
      teamId,
    });
    if (isFreeUser) {
      dispatch(
        pushModal({
          type: 'stripe-pricing-table-upgrade-modal',
          source: 'companies',
        }),
      );
      return;
    }
    setShowCompaniesModal(true);
    setLoadingNavItem(null);
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  // Main navigation items
  const navigationItems: NavigationItem[] = [
    {
      key: 'recents',
      label: t('drawer.quickActions.home.label'),
      icon: <Home className="h-4 w-4" strokeWidth={1.75} />,
      tooltip: t('drawer.quickActions.home.tooltip'),
      href: getFolderUrl({
        teamId,
        folderId: 'recents',
        withBasePath: false,
      }),
      onClick: handleQuickActionClick,
      isActive: folderId === 'recents',
    },
  ];

  // Feature items
  const featureItems: NavigationItem[] = [
    {
      key: 'search',
      label: t('drawer.search.title'),
      icon: <SearchIcon className="h-4 w-4" strokeWidth={1.75} />,
      tooltip: t('drawer.search.title'),
      onClick: handleSearchClick,
    },
    {
      key: 'people',
      label: t('drawer.actions.people', 'People'),
      icon: <Users className="h-4 w-4" strokeWidth={1.75} />,
      tooltip: t('drawer.actions.people', 'People'),
      onClick: handlePeopleClick,
      isActive: false,
    },
    {
      key: 'companies',
      label: t('drawer.actions.companies', 'Companies'),
      icon: <Building className="h-4 w-4" strokeWidth={1.75} />,
      tooltip: t('drawer.actions.companies', 'Companies'),
      onClick: handleCompaniesClick,
      isActive: false,
    },
    {
      key: 'download-app',
      label: t('drawer.actions.downloadApp', 'Download App'),
      icon: <Download className="h-4 w-4" strokeWidth={1.75} />,
      tooltip: t('drawer.actions.downloadApp', 'Download App'),
      onClick: handleDownloadAppClick,
      isActive: false,
    },
    {
      key: 'chrome-extension',
      label: t('drawer.actions.chromeExtension', 'Get Chrome Extension'),
      icon: <Chrome className="h-4 w-4" strokeWidth={1.75} />,
      tooltip: t('drawer.actions.chromeExtension', 'Get Chrome Extension'),
      onClick: handleChromeExtensionClick,
      isActive: false,
      hidden: isExtensionInstalled,
    },
  ];

  const renderNavigationItem = (item: NavigationItem) => {
    if (item.hidden) return null;

    const handleClick = () => {
      // Only set loading state if we're navigating to a different item
      if (!item.isActive) {
        setLoadingNavItem(item.key);
      }

      if (item.href) {
        navigate(item.href);
      }
      if (item.onClick) {
        item.onClick();
      }
    };

    const isExternalLink =
      item.key === 'download-app' || item.key === 'chrome-extension';
    const isLoading = loadingNavItem === item.key;

    const buttonContent = (
      <div className="w-full relative group/nav-item rounded-md">
        <Button
          variant="ghost"
          size="sm"
          className={`w-full rounded-md flex items-center justify-start gap-3 px-3 transition-all duration-200 group-hover/nav-item:pr-7 ${
            item.isActive
              ? 'bg-gray-100 dark:bg-muted text-gray-900 dark:text-gray-100 [&_svg]:text-gray-900 dark:[&_svg]:text-gray-100'
              : 'text-gray-500 dark:text-gray-400 [&_svg]:text-gray-500 dark:[&_svg]:text-gray-400 group-hover/nav-item:bg-gray-100 dark:group-hover/nav-item:bg-muted group-hover/nav-item:text-gray-700 dark:group-hover/nav-item:text-gray-200 group-hover/nav-item:[&_svg]:text-gray-700 dark:group-hover/nav-item:[&_svg]:text-gray-200'
          }`}
          onClick={handleClick}
        >
          {isLoading ? (
            <Loader className="h-4 w-4 animate-spin" strokeWidth={1.75} />
          ) : (
            item.icon
          )}
          <span
            className={`text-sm transition-opacity duration-100 flex-1 text-left truncate ${open ? 'opacity-100' : 'opacity-0'}`}
          >
            {item.label}
          </span>
        </Button>
        {isExternalLink && open && (
          <div
            role="button"
            tabIndex={0}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 transition-opacity opacity-0 group-hover/nav-item:opacity-100 pointer-events-auto cursor-pointer flex items-center justify-center h-6 w-6 text-gray-400"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleClick();
              }
            }}
          >
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </div>
        )}
      </div>
    );

    return (
      <Tooltip key={item.key}>
        <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
        {!open && (
          <TooltipContent side="right">
            {item.tooltip || item.label}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-1 px-2 py-1">
        {/* Main Navigation */}
        <div className="flex flex-col gap-1">
          {navigationItems.map(renderNavigationItem)}
        </div>

        {/* Feature Items */}
        <div className="flex flex-col gap-1">
          {featureItems.map(renderNavigationItem)}
        </div>
      </div>

      {showTrialModal && <FreeTrial onClose={() => setShowTrialModal(false)} />}
      {showSearchDialog && (
        <SearchDialog
          open={showSearchDialog}
          onClose={() => {
            setShowSearchDialog(false);
            setLoadingNavItem(null);
          }}
          teamId={teamId}
        />
      )}
      {showPeopleModal && (
        <PeopleModal
          open={showPeopleModal}
          onClose={() => {
            setShowPeopleModal(false);
            setLoadingNavItem(null);
          }}
        />
      )}
      {showCompaniesModal && (
        <CompaniesModal
          open={showCompaniesModal}
          onClose={() => {
            setShowCompaniesModal(false);
            setLoadingNavItem(null);
          }}
        />
      )}
    </TooltipProvider>
  );
};

export default NavigationActions;
